import { VETERAN_INFO_FIELDS, MILITARY_HISTORY_FIELDS } from './fieldMapping';

const CONFLICT_FIELDS = [...VETERAN_INFO_FIELDS, ...MILITARY_HISTORY_FIELDS];

// Blank = null/undefined, empty string, or an object with no truthy leaf
// (mirrors isBlankValue in cave/domain/conflictDetection).
const isBlank = value =>
  value == null ||
  value === '' ||
  (typeof value === 'object' && !Object.values(value).some(Boolean));

// The CAVE conflict-resolution fields (veteran/decedent name, SSN, DOB, date of death, branch
// of service, service entry/separation dates) are corrected on the top-level 534EZ/8416 form
// only -- ConflictPageField writes the resolved value via applyToForm and never touches
// files[].idpArtifacts, so those entries keep the raw CAVE extraction. idpArtifacts feeds the
// 21-4138 change log AND the MMS/BPDS submissions, so at submit we copy each corrected
// top-level value into every matching artifact entry, making idpArtifacts the user-final source
// of truth (consistent with the artifact-only fields, which are edited in idpArtifacts directly
// and are intentionally left untouched here).
//
// Must run BEFORE splitVaSsnField so veteranSocialSecurityNumber is still { ssn } for
// getFormValue. Submit-time only -- does not mutate Redux, so the live conflict UI is unaffected.
//
// Gated on the CAVE (IDP) feature toggle `survivorsBenefitsIdp` (synced into form data via
// useFormFeatureToggleSync in SurvivorsBenefitsApp). When the toggle is off, the submission is returned
// untouched so idpArtifacts keep their raw CAVE values (pre-fix behavior).
export function reconcileCaveArtifacts(formData) {
  const parsed = JSON.parse(formData);

  if (!parsed?.survivorsBenefitsIdp) {
    return formData;
  }

  const { files } = parsed;

  if (!Array.isArray(files) || files.length === 0) {
    return formData;
  }

  let changed = false;

  const nextFiles = files.map(file => {
    if (!file?.idpArtifacts) return file;

    let artifacts = file.idpArtifacts;

    CONFLICT_FIELDS.forEach(fieldDef => {
      const formValue = fieldDef.getFormValue(parsed);
      if (isBlank(formValue)) return;

      fieldDef.artifacts.forEach(artifact => {
        const entries = artifacts[artifact.artifactKey];
        if (!Array.isArray(entries) || entries.length === 0) return;

        const nextEntries = entries.map(entry =>
          artifact.setArtifactValue(entry, formValue),
        );
        if (JSON.stringify(nextEntries) !== JSON.stringify(entries)) {
          artifacts = { ...artifacts, [artifact.artifactKey]: nextEntries };
          changed = true;
        }
      });
    });

    return artifacts === file.idpArtifacts
      ? file
      : { ...file, idpArtifacts: artifacts };
  });

  return changed ? JSON.stringify({ ...parsed, files: nextFiles }) : formData;
}
